import React from 'react';
import { Controller } from 'react-hook-form';

import Field, { FieldProps } from 'ui/Field';
import Group, { GroupProps } from 'ui/Group';
import Input, { InputAddon, InputProps } from 'ui/Input';
import Tooltip from 'ui/Tooltip';
import { useUpstreamFormPropagation } from 'utils/form';
import { MAX_TEXT_VALUE_LENGTH } from 'utils/validation';

import { useSankeyFormContext } from '../../contexts/sankeyFormContext';
import FormCheckbox from './FormCheckbox';

type Props = Omit<GroupProps, 'form'> & {
  label?: React.ReactNode;
  checkboxField: SankeyFormBooleanField;
  textField: SankeyFormTextField;
  fieldFontSize?: FieldProps['fontSize'];
  placeholder?: string;
  size?: InputProps['size'];
};

const FormToggleableTextInput = ({
  label,
  checkboxField,
  textField,
  placeholder,
  fieldFontSize,
  size,
  ...props
}: Props) => {
  const { form } = useSankeyFormContext();
  const isChecked = form.watch(checkboxField);

  // Propagate the text field value after 200ms to avoid too many re-renders while typing.
  // The checkbox field is propagated internally by the FormCheckbox component.
  useUpstreamFormPropagation(textField, 200);

  return (
    <Controller
      name={textField}
      control={form.control}
      render={({ field, fieldState }) => (
        <Field
          label={label}
          invalid={fieldState.invalid}
          errorText={fieldState.error?.message}
          fontSize={fieldFontSize}
        >
          <Group attached w="100%" {...props}>
            <InputAddon>
              <FormCheckbox id={checkboxField} field={checkboxField} />
            </InputAddon>
            {isChecked ? (
              <Input
                name={field.name}
                value={field.value ?? undefined}
                onChange={field.onChange}
                maxLength={MAX_TEXT_VALUE_LENGTH}
                size={size}
              />
            ) : (
              <Tooltip
                content="Click the checkbox to overwrite this value"
                portalled
              >
                <Input
                  key="disabled" // Force input rerender to not mess up form values
                  placeholder={placeholder ?? `${form.getValues(textField)}`}
                  size={size}
                  disabled
                />
              </Tooltip>
            )}
          </Group>
        </Field>
      )}
    />
  );
};

export default FormToggleableTextInput;
